// src/components/auth/ParentSignup.tsx - CHILD-CENTERED SIGNUP FLOW
import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'wouter'
import { useParentAuth } from '../../contexts/ParentAuthContext'
import { httpsCallable } from 'firebase/functions'
import { Mail, Lock, User, ArrowRight, ArrowLeft, Plus, Check, Shield, Heart, Star, CheckCircle, X, Eye, EyeOff } from 'lucide-react'
import { 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth'
import { doc, setDoc, collection, addDoc } from 'firebase/firestore'
import { auth, db, functions } from '../../lib/firebase-config'
import { Button } from '@/components/ui/Button'
import bcrypt from 'bcryptjs'
import type { ParentProfile, ChildProfile } from '../../types/auth'
import { SECURITY_QUESTIONS, type SecurityQuestion, type RecoveryAttempts } from '../../types/auth'
import { checkUsernameExists } from '../../utils/usernameValidation'

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

// Create Google provider OUTSIDE component to persist across renders
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')  

export const ParentSignup: React.FC = () => {
  const [step, setStep] = useState(1) // 1: Auth method, 2: Email verify, 3: Success
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [checkingVerification, setCheckingVerification] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  // Parent data
  const [parentData, setParentData] = useState({
    email: '',
    password: ''
  })
 
  const [createdParentId, setCreatedParentId] = useState('')
  const [, navigate] = useLocation()

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current)
      }
    }
  }, [])

  // === STEP 1: PARENT REGISTRATION ===
  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Email validation
    if (!isValidEmail(parentData.email)) {
      setError('Please enter a valid email address')
      setLoading(false)
      return
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        parentData.email, 
        parentData.password
      )
      
      // Send verification email
      const sendVerification = httpsCallable(functions, 'sendVerificationEmail')
      await sendVerification({ 
        email: userCredential.user.email,
        displayName: userCredential.user.email!.split('@')[0]
      })

      await createParentProfile(userCredential.user, 'email')
      setCreatedParentId(userCredential.user.uid)
      setStep(2) // Go to email verification screen first
      
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('This email already has an account.')
      } else {
        setError(err.message || 'Failed to create account')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setLoading(true)
    setError('')

    try {
      const result = await signInWithPopup(auth, googleProvider)
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser
      
      if (isNewUser) {
        await createParentProfile(result.user, 'google')
      } else {
        navigate('/parent')
      }
      
    } catch (err: any) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/cancelled-popup-request') {
        const confirmed = confirm('Popups are blocked. Use slower redirect method instead?')
        if (confirmed) {
          await signInWithRedirect(auth, googleProvider)
        } else {
          setLoading(false)
        }
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setLoading(false)
        setError(err.message || 'Google signup failed')
      } else {
        setLoading(false)
      }
    }
  }

  const createParentProfile = async (user: any, provider: 'email' | 'google') => {
    const parentProfile: ParentProfile = {
      parentId: user.uid,
      email: user.email!,
      displayName: user.displayName || parentData.displayName || user.email!.split('@')[0],
      photoURL: user.photoURL || null,
      authProvider: provider,
      createdAt: new Date(),
      lastLoginAt: new Date(),
      subscription: { tier: 'free', status: 'active' },
      children: []
    }
    
    await setDoc(doc(db, 'parents', user.uid), parentProfile)
    
    // Send welcome emails
    const sendWelcome = httpsCallable(functions, 'sendWelcomeEmails')
    await sendWelcome({
      email: parentProfile.email,
      displayName: parentProfile.displayName,
      userType: 'parent'
    })
    
    navigate('/parent')
  }

  const handleForgotPassword = async () => {
    if (!parentData.email) {
      setError('Please enter your email address first')
      return
    }
  
    if (!isValidEmail(parentData.email)) {
      setError('Please enter a valid email address')
      return
    }
  
    setLoading(true)
    setError('')
  
    try {
      await sendPasswordResetEmail(auth, parentData.email)
      setError('') // Clear any errors
      // You might want to show a success message instead of using error state
      alert('Password reset email sent! Check your inbox.')
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return
    if (!auth.currentUser) {
      setError('Session expired. Please refresh and try again.')
      return
    }
    
    try {
      await sendEmailVerification(auth.currentUser)
      setVerificationEmailSent(true)
      setResendCooldown(60) // 60 second cooldown
      
      // Countdown timer
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      setError(err.message || 'Failed to resend email')
    }
  }
  
  const handleCheckVerification = async () => {
    if (!auth.currentUser) {
      setError('Session expired. Please refresh and try again.')
      return
    }
    
    setCheckingVerification(true)
    try {
      await auth.currentUser.reload() // Refresh user data
      
      if (auth.currentUser.emailVerified) {
        // Send welcome email now that they're verified
        const sendWelcome = httpsCallable(functions, 'sendWelcomeEmails')
        await sendWelcome({ 
          email: auth.currentUser.email,
          displayName: auth.currentUser.email!.split('@')[0],
          userType: 'parent' 
        }).catch(console.error)

        navigate('/parent') 
      } else {
        setError('Email not verified yet. Please check your inbox and click the link.')
      }
    } catch (err: any) {
      setError('Failed to check verification status')
    } finally {
      setCheckingVerification(false)
    }
  }

  const goToApp = () => {
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
      <div className="w-full max-w-sm sm:max-w-md lg:max-w-2xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                             ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 1 ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div className={`w-8 h-1 ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                             ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 2 ? <Check className="w-4 h-4" /> : '2'}
              </div>
              <div className={`w-8 h-1 ${step >= 3 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                             ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 3 ? <Check className="w-4 h-4" /> : '3'}
              </div>
              <div className={`w-8 h-1 ${step >= 4 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                             ${step >= 4 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 4 ? <Check className="w-4 h-4" /> : '4'}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 
                          dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-300 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* STEP 1: Parent Registration */}
          {step === 1 && (
            <div>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-semibold text-[#1F2937] mb-2">
                  Create Account
                </h1>
                <p className="text-[#6B7280]">
                  Set up your family dashboard
                </p>
              </div>

              {/* Google Signup */}
              <Button
                type="button"
                onClick={handleGoogleSignup}
                disabled={loading}
                className="w-full mb-6 bg-white dark:bg-gray-700 border border-gray-300 
                         dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 
                         dark:hover:bg-gray-600 py-4 px-4 min-h-[44px] rounded-lg font-medium 
                         transition-colors duration-200 flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center mb-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="mx-4 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Email Signup Form */}
              <form onSubmit={handleEmailSignup} className="space-y-4">
          {/*<div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={parentData.displayName}
                    onChange={(e) => setParentData({ ...parentData, displayName: e.target.value })}
                    className="w-full px-4 py-4 text-base border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your name"
                  />
                </div>*/}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={parentData.email}
                    onChange={(e) => setParentData({ ...parentData, email: e.target.value })}
                    className="w-full px-4 py-4 text-base border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={parentData.password}
                    onChange={(e) => setParentData({ ...parentData, password: e.target.value })}
                    className="w-full px-4 py-4 text-base border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Create a secure password"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white py-4 px-4 min-h-[44px] 
                           rounded-lg font-medium transition-colors duration-200 
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Help Set Up'}
                </Button>
                <div className="text-center mt-4">
                <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="text-sm text-[#10B981] hover:text-[#059669] 
                            dark:hover:text-indigo-300 underline transition-colors duration-200
                            disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Forgot your password?
                </button>
                </div>
              </form>
            </div>
          )}

          {/* STEP 2: EMAIL VERIFICATION */}
          {step === 2 && (
            <div className="text-center space-y-6">
              <div className="bg-indigo-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto">
                <Mail className="w-10 h-10 text-indigo-600" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Check Your Email
                </h2>
                <p className="text-gray-600 mb-4">
                  We sent a verification link to:<br />
                  <span className="font-semibold text-gray-900">{parentData.email}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Click the link in the email to verify your account, then come back here.
                </p>
              </div>

              {error && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                  {error.includes('already has an account') && (
                    <Button
                      onClick={() => window.location.href = '/auth/parent-login'}
                      className="w-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    >
                      Login Instead
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={handleCheckVerification}
                  disabled={checkingVerification}
                  className="w-full"
                >
                  {checkingVerification ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Checking...
                    </div>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I've Verified My Email
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0}
                  className="w-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  {resendCooldown > 0 
                    ? `Resend in ${resendCooldown}s` 
                    : 'Resend Verification Email'
                  }
                </Button>
              </div>

              <p className="text-xs text-gray-500">
                Can't find the email? Check your spam folder.
              </p>
            </div>
          )}

          {/* STEP 2: Ownership Transition - THE MAGIC MOMENT */}
          {step === 3 && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="p-6 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full">
                  <Star className="w-16 h-16 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-6">
                Now It's YOUR Turn! 🌟
              </h1>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-6 rounded-xl mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                  <Heart className="w-8 h-8 text-red-500" />
                  <Star className="w-8 h-8 text-yellow-500" />
                </div>
                
                <div className="space-y-4 text-left max-w-md mx-auto">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">
                      This reading space belongs to <strong>YOU</strong>
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">
                      Your reading practice is <strong>private</strong> - only you can see your progress
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">
                      Parents can only see what <strong>YOU decide</strong> to share with them
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">
                      <strong>You're in control</strong> of your learning adventure here
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  🏆 Ready to create your own special reading username and secret PIN?
                </p>
              </div>

              <Button
                type="button"
                onClick={() => setStep(4)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 
                         text-white py-4 px-8 rounded-xl font-bold text-lg
                         transition-all duration-200 transform hover:scale-105
                         flex items-center justify-center gap-3 mx-auto shadow-lg"
              >
                Yes! Create MY Space
                <ArrowRight className="w-6 h-6" />
              </Button>
            </div>
          )}

          {/* Back to Login Link */}
          {step === 1 && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Already have an account?
              </p>
              <button
                onClick={() => window.location.href = '/parent-login'}
                className="text-[#10B981] hover:text-[#059669] 
                         dark:hover:text-indigo-300 underline transition-colors duration-200"
              >
                Click to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}